import { useCallback, useEffect, useRef, useState } from 'react'

import { DEFAULT_CONCURRENCY } from './concurrency'

/**
 * One request per row, bounded, with a state for each row.
 *
 * History lists recordings; the score beside each comes from a separate
 * `GET /recordings/{id}/daily-status`. §4.1 wants that fan-out bounded, with
 * **visible per-row loading and error states**, and no request at all for rows
 * that cannot have a result.
 *
 * ── WHY `runBounded` IS NOT ENOUGH ON ITS OWN ───────────────────────────────
 *
 * It is the right primitive, and this is the same scheduling shape. Four things
 * a screen needs are outside its contract:
 *
 * 1. **Per-row progress.** `runBounded` resolves once, after everything settles,
 *    so a list driven by it has no way to show a row as loading while the batch
 *    runs. This publishes a transition per row.
 * 2. **Eligibility is a skip, not a task.** An `empty` recording has no Daily
 *    Status by construction — `daily_status_uri` is null and the route answers
 *    404 — and §2.6 is explicit that `empty` is not an error. Asking and then
 *    swallowing the 404 would be a request that manufactures the very failure
 *    state the spec forbids rendering.
 * 3. **Memory across page appends.** `loadMore` grows the key list; without a
 *    cache, every page re-requests the whole history, and §4.5's "keep chart
 *    requests bounded as pages load" fails on the second page.
 * 4. **Abort.** `runBounded` takes no signal. An unmount or a sign-out mid-scan
 *    must stop the queue and drop what comes back.
 */

export type FanOutStatus = 'skipped' | 'pending' | 'loading' | 'ready' | 'error'

export interface FanOutEntry<V> {
  status: FanOutStatus
  value?: V
  error?: unknown
}

export interface FanOut<K extends string, V> {
  entries: Readonly<Record<K, FanOutEntry<V>>>
  /** Re-queue one row. §4.1's per-row error, made actionable. */
  retry: (key: K) => void
}

export interface FanOutOptions<K> {
  /** False means `skipped`, and **no request is made**. */
  eligible: (key: K) => boolean
  limit?: number
}

export function useFanOut<K extends string, V>(
  keys: readonly K[],
  fetchOne: (key: K, signal: AbortSignal) => Promise<V>,
  { eligible, limit = DEFAULT_CONCURRENCY }: FanOutOptions<K>,
): FanOut<K, V> {
  const [entries, setEntries] = useState<Record<string, FanOutEntry<V>>>({})
  // What has been queued at least once, so a growing key list only adds work.
  // A ref rather than state: it must not itself trigger a render, and it must be
  // readable by the effect without joining its dependency list.
  const seen = useRef(new Set<string>())
  const [retryNonce, setRetryNonce] = useState(0)

  const retry = useCallback((key: K) => {
    seen.current.delete(key)
    setRetryNonce((n) => n + 1)
  }, [])

  /**
   * A new array holding the same ids must not requeue anything, and a caller
   * that rebuilds its list every render is the normal case.
   *
   * The effect below depends on THIS and not on `keys`, which is load-bearing
   * rather than tidy. With `keys` in the dependency list, an unmemoised caller
   * -- `rows.map((r) => r.recording_id)` written inline, which is what anybody
   * writes first -- re-ran the effect on every render, and the effect's own
   * first `setEntries` causes a render. Before the cleanup below learned to
   * un-mark unsettled keys that froze every row on `loading`; after it, it is an
   * unbounded request loop. Depending on the ids by value adds no assumption:
   * the join already required that an id contains no space, which the backend's
   * `session_id` format guarantees.
   */
  const keyList = keys.join(' ')

  useEffect(() => {
    const controller = new AbortController()
    let cancelled = false
    // Captured inside the effect so the cleanup below cannot read a ref that has
    // since been replaced -- the lint rule's point, and a real hazard here
    // because the cleanup un-marks keys it never got to.
    const queuedOnce = seen.current

    const fresh = (keyList === '' ? [] : (keyList.split(' ') as K[])).filter(
      (key) => !queuedOnce.has(key),
    )
    if (fresh.length === 0) return

    for (const key of fresh) queuedOnce.add(key)

    const skipped = fresh.filter((key) => !eligible(key))
    const queued = fresh.filter((key) => eligible(key))

    setEntries((previous) => {
      const next = { ...previous }
      for (const key of skipped) next[key] = { status: 'skipped' }
      for (const key of queued) next[key] = { status: 'pending' }
      return next
    })

    let index = 0
    // WHAT ACTUALLY FINISHED, which is not the same as what was claimed. See the
    // cleanup below for why the difference is a row that spins for ever.
    const settled = new Set<K>()
    const worker = async () => {
      for (;;) {
        const current = index
        index += 1
        if (current >= queued.length || cancelled) return
        const key = queued[current]
        setEntries((previous) => ({ ...previous, [key]: { status: 'loading' } }))
        try {
          const value = await fetchOne(key, controller.signal)
          if (cancelled) return
          settled.add(key)
          setEntries((previous) => ({ ...previous, [key]: { status: 'ready', value } }))
        } catch (error) {
          if (cancelled || controller.signal.aborted) return
          settled.add(key)
          // One row's failure is one row's failure. The other forty-nine stay.
          setEntries((previous) => ({ ...previous, [key]: { status: 'error', error } }))
        }
      }
    }

    void Promise.all(Array.from({ length: Math.max(1, Math.min(limit, queued.length)) }, worker))

    return () => {
      cancelled = true
      controller.abort()
      /**
       * ── ANYTHING THAT DID NOT FINISH MAY BE ASKED AGAIN ────────────────
       *
       * This used to un-mark `queued.slice(index)` -- the keys never CLAIMED.
       * But `index` is incremented above BEFORE the bounds check and before the
       * await, so the keys at `0..index-1` are claimed and in flight. Those
       * stayed marked in `queuedOnce` while `controller.abort()` killed their
       * requests and `cancelled` swallowed the rejection, so the next run's
       * `fresh` filter skipped them and nothing ever finished them. Up to
       * `limit` rows kept a spinner for the life of the screen, with no retry
       * offered and no error to explain it.
       *
       * It fired whenever the key list GREW while a request was open -- every
       * "Load more" on a real connection. `settled` is the honest predicate:
       * a key that reached a terminal entry is done, and everything else is not.
       */
      for (const key of queued) if (!settled.has(key)) queuedOnce.delete(key)
    }
  }, [keyList, fetchOne, eligible, limit, retryNonce])

  return { entries: entries as Readonly<Record<K, FanOutEntry<V>>>, retry }
}
